<template>
  <b-container fluid>
    <filter-search
      :defaultDate="$route.params.defaultDate"
      :ecr="true"
      ref="filterSearch"
      @onSelectDate="loadPackages(1)"
      @onSearch="loadPackagesBySearchButton(1)"
    ></filter-search>
    <DragCol width="100%" height="100%">
      <template #left>
        <b-table
          hover
          show-empty
          responsive
          ref="packages"
          id="data_table"
          :fields="ecrFields"
          :items="ecrItems"
          :busy="visible"
          :no-provider-paging="true"
          :no-provider-sorting="true"
          :no-provider-filtering="true"
          :current-page="currentPage"
          :per-page="perPage"
          :filter="filter"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @filtered="onFiltered"
          @row-hovered="rowHowered"
          @row-unhovered="rowUnhowered"
          class="mt-3"
          empty-text="Нет данных в таблице"
          empty-filtered-text="Нет данных по вашему запросу"
          selectable
          select-mode="single"
          @row-selected="showHouseShipments"
          selected-variant="secondary"
          :sort-compare="mySortCompare"
        >
          <template v-slot:cell(package)="data">
            <b><p class="mb-0">{{ data.item.pid }}<span v-if="data.item.pid !== data.item.chainid">/{{ data.item.chainid }}</span></p></b>
            <div class="status-wrapper d-flex align-items-center">
              <b-badge :variant="convertToStatusColor(data.item.status)">{{ data.item.status_name }}</b-badge>
              <div v-if="data.item.decline_reasons.length > 0" @click.stop="showDeclineReasons(data.item.decline_reasons)" class="reply-icon-wrapper ml-2">
                <font-awesome-icon class="text-danger" icon="question-circle"/>
              </div>
            </div>
            <b-button
              :href="data.item.docsign_url"
              target="_blank"
              class="d-flex align-items-center mt-2"
              style="max-width: 110px"
              size="sm"
              v-if="data.item.docsign_url && (data.item.docsign_url !== null)"
              variant="primary">
              <font-awesome-icon icon="fa-solid fa-fingerprint" class="mr-1" />Подписать
            </b-button>
          </template>
          <template v-slot:cell(waybill)="data">
            <div v-if="data.item.info[1] && ((data.item.info[1].length - 1) > 9)" class="mb-0 pl-4">
              <div v-for="(item, index) in data.item.info[1]" :key="item">
                <span v-if="index < 9">{{ item }}, </span>
                <span v-else-if="index === 9">{{ item }}...</span>
              </div>
            </div>
            <div v-else class="mb-0 pl-4">
              <div v-for="(item, index) in data.item.info[1]" :key="item">
                <span v-if="(index < (data.item.info[1].length - 1))">{{ item }}, </span>
                <span v-else>{{ item }}</span>
              </div>
            </div>
          </template>
          <template v-slot:cell(vehicle)="data">
            <div v-if="data.item.info[4] && ((data.item.info[4].length - 1) > 9)" class="mb-0 pl-4">
              <div v-for="(item, index) in data.item.info[4]" :key="item">
                <span v-if="index < 9">{{ item }}, </span>
                <span v-else-if="index === 9">{{ item }}...</span>
              </div>
            </div>
            <div v-else class="mb-0 pl-4">
              <div v-for="(item, index) in data.item.info[4]" :key="item">
                <span v-if="(index < (data.item.info[4].length - 1))">{{ item }}, </span>
                <span v-else>{{ item }}</span>
              </div>
            </div>
          </template>
          <template v-slot:cell(organization)="data">
            <div v-if="data.item.info[3] && ((data.item.info[3].length - 1) > 3)" class="mb-0 pl-4">
              <div v-for="(item, index) in data.item.info[3]" :key="index">
                <span v-if="index < 3">{{ item }}, </span>
                <span v-else-if="index === 3">{{ item }}...</span>
              </div>
            </div>
            <div v-else class="mb-0 pl-4">
              <div v-for="(item, index) in data.item.info[3]" :key="index">
                <span v-if="(index < (data.item.info[3].length - 1))">{{ item }}, </span>
                <span v-else>{{ item }}</span>
              </div>
            </div>
          </template>
          <template v-slot:cell(modify_datetime)="data">
            <div class="user-creator-wrapper">
              <b-button class="icon-wrapper" v-b-tooltip.hover title="Создатель">
                <font-awesome-icon icon="user"/>
              </b-button>
              <span class="user-creator">{{ data.item.sender }}</span>
            </div>
            <div class="created-date-wrapper">
              <b-button class="icon-wrapper" v-b-tooltip.hover :title="getDateTooltip(data.item.creationdate, data.item.getlastmodified)">
                <font-awesome-icon icon="clock"/>
              </b-button>
              <span class="created-date">{{ getDate(data.item.creationdate, data.item.getlastmodified) }}</span>
            </div>
            <div class="destination-wrapper">
              <b-button class="icon-wrapper" v-b-tooltip.hover title="Получатель">
                <font-awesome-icon icon="arrow-alt-circle-right"/>
              </b-button>
              <span class="destination"><b-badge :variant="convertToDirectionColor(data.item.receiver)">{{ convertToDirection(data.item.receiver) }}</b-badge></span>
            </div>
          </template>
          <template v-slot:cell(actions)="row">
            <actions
              ref="actions"
              :visible="row.item.actions.visible"
              ecr="true"
              :target="row.item.pid.toString()"
              :status="row.item.status"
              @open_documents="openDocuments(row)"
              @edit_package="editPackage(row)"
              @edit_package_as_new="editPackageAsNew(row)">
            </actions>
          </template>
          <template slot="row-details" slot-scope="row">
            <card :attachmentWidth="attachmentWidth" :row="row" @onOpenDocument="openDocument($event)"></card>
          </template>
          <div slot="table-busy" class="text-xl-center text-lg-center text-md-center text-sm-left text-left text-secondary my-2">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Загружаем данные...</strong>
          </div>
        </b-table>
        <b-pagination align="right"
                      prev-text="&lt;"
                      first-text="&lt;&lt;"
                      next-text="&gt;"
                      last-text="&gt;&gt;"
                      size="sm"
                      :total-rows="pages*10*2"
                      :per-page="perPage"
                      @change="loadPackagesByPagination"
                      v-model="currentPage">
        </b-pagination>
      </template>
      <template #right>
        <HouseShipmentList :ecr="true" :ecrPid="selectedEcrPid"/>
      </template>
    </DragCol>
    <open-document-modal parentType="Ecr" @resetModalProps="resetProps"></open-document-modal>
    <b-modal id="declineReasonsEcr" class="text-center" size="md" hide-footer>
      <template slot="modal-title">
        <div v-html="'Причины отклонения пакета'"></div>
      </template>
      <div v-for="(item, index) in declineReasons" :key="index" style="font-size: 14px">
        <div>{{ index + 1 }}. {{ item.ResultDescription }}</div>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
  import { mapState, mapActions, mapMutations } from 'vuex'
  import FilterSearch from './EcrFilterSearch'
  import moment from 'moment'
  import { converterMixin } from '../mixins/converterMixin'
  import Actions from './HouseShipmentListActions'
  import Card from '../DocumentCard'
  import DragCol from './DragCol'
  import HouseShipmentList from './HouseShipmentList'
  import { processDocumentsMixin } from '../mixins/processDocumentsMixin'
  import OpenDocumentModal from '../modals/OpenDocumentEcr'

  export default {
    name: "EcrList",
    mixins: [converterMixin, processDocumentsMixin],
    components: {
      FilterSearch,
      Actions,
      DragCol,
      Card,
      HouseShipmentList,
      OpenDocumentModal
    },
    data () {
      return {
        ecrFields: [
          { key: 'package', label: 'РЭГ' },
          { key: 'waybill', label: '№ накладной' },
          { key: 'organization', label: 'Организации' },
          { key: 'vehicle', label: 'Транспорт' },
          { key: 'modify_datetime', label: 'Параметры' },
          { key: 'actions', label: 'Действия', tdClass: '', stickyColumn: true, thStyle: { width: "25%" } }
        ],
        modified: false,
        ecrItems: [],
        houseShipmentItems: [],
        currentPage: 1,
        currentHouseShipmentPage: 1,
        totalRows: 1,
        filter: null,
        perPage: 0,
        sortBy: 'modify_datetime',
        sortDesc: false,
        attachmentWidth: null,
        declineReasons: [],
        selectedEcrPid: null,
        documentAdditionalTable: null,
        documentGeneralInformation: null,
        documentName: null,
        documentHtml: null,
        documentXml: null,
        documentId: null,
        documentHtmlForPdf: null,
        documentFileName: null,
        mergedDocumentXml: null,
      }
    },
    watch: {},
    computed: {
      ...mapState(
        {
          ecrPackages: state => state.ecr.packages,
          visible: state => state.ecr.visible,
          statuses: state => state.packages.statuses,
          pages: state => state.ecr.pages,
          pids: state => state.ecr.pids,
          packageDocumentsList: state => state.ecr.packageDocumentsList,
          documents: state => state.ecr.documents,
        }
      )
    },
    mounted () {
      this.loadPackages(1)
      localStorage.removeItem('decline_reasons')
    },
    methods: {
      showHouseShipments (item) {
        if (item.length > 0) {
          this.ecrItems.map((ecr) => {
            if (ecr.pid === item[0].pid) {
              ecr._rowVariant = 'secondary'
              return ecr
            } else {
              delete(ecr._rowVariant)
            }
          })
          this.selectedEcrPid = item[0].pid
        } else {
          this.selectedEcrPid = null
          this.ecrItems.map((ecr) => delete(ecr._rowVariant))
        }
      },
      getDatepickerDate () {
        let ISODate = null
        if (this.$refs.filterSearch.datePickerValue !== null) {
          if (this.$refs.filterSearch.datePickerValue.end !== null) {
            const start = this.moment(this.$refs.filterSearch.datePickerValue.start).add(3, 'hours').format('YYYY-MM-DD HH:mm')
            const end = this.moment(this.$refs.filterSearch.datePickerValue.end).add(3, 'hours').format('YYYY-MM-DD HH:mm')
            ISODate = {
              datetime_start: new Date(start.replace(/-/g, '/').replace('T', ' ')).toISOString(),
              datetime_end: new Date(end.replace(/-/g, '/').replace('T', ' ')).toISOString()
            }
            localStorage.setItem('datePickerValue_start', this.$refs.filterSearch.datePickerValue.start.replace(/-/g, '/').replace('T', ' '))
            localStorage.setItem('datePickerValue_end', this.$refs.filterSearch.datePickerValue.end.replace(/-/g, '/').replace('T', ' '))
          } else {
            const start = this.moment(this.$refs.filterSearch.datePickerValue.start).add(3, 'hours').format('YYYY-MM-DD HH:mm')
            const end = this.moment(start).add(1, 'days').subtract(1, 'seconds').format('YYYY-MM-DD HH:mm')
            this.$refs.filterSearch.datePickerValue.end = this.moment(this.$refs.filterSearch.datePickerValue.start).add(1, 'days').subtract(1, 'seconds').format('YYYY-MM-DD HH:mm')
            ISODate = {
              datetime_start: new Date(start.replace(/-/g, '/').replace('T', ' ')).toISOString(),
              datetime_end: new Date(end.replace(/-/g, '/').replace('T', ' ')).toISOString()
            }
            localStorage.setItem('datePickerValue_start', start.replace(/-/g, '/').replace('T', ' '))
            localStorage.setItem('datePickerValue_end', end.replace(/-/g, '/').replace('T', ' '))
          }
        }
        return ISODate
      },
      moment: function (date) {
        moment.locale('ru')
        return moment(date)
      },
      loadPackagesByPagination (page) {
        this.loadPackages(page, { pagination: true }, { search: false })
      },
      loadPackagesBySearchButton (page) {
        this.loadPackages(page, { pagination: false }, { search: true })
      },
      loadPackages (page, obj = { pagination: false }, search = { search: false }) {
        this.houseShipmentItems = []
        this.setVisible(true)
        return new Promise((resolve, reject) => {
          const params = {
            page: page,
            key: this.$refs.filterSearch.searchText, // общий поиск
            pid: this.$refs.filterSearch.searchTextByPid, // поиск по номеру пакета
            statuses: (this.$refs.filterSearch.selectedStatus === null) ? [] : this.$refs.filterSearch.selectedStatus, // фильтр по статусам
            pids: ''
          }
          if (search.search === true) {
            this.currentPage = 1
          }
          if (obj.pagination === true) {
            const myPids = this.pids.slice()
            params.pids = myPids.splice(((page - 1) * 10), 10)
          }
          if (!this.$refs.filterSearch.searchByPackageNumber) {
            Object.assign(params, this.getDatepickerDate())
            this.getStatuses({ portalId: this.$refs.filterSearch.portalId, queryParams: this.getDatepickerDate() }).then(
              () => {
                this.getEcrPackages({ portalId: this.$refs.filterSearch.portalId, queryParams: params }).then(
                  () => {
                    this.ecrItems = JSON.parse(JSON.stringify(this.ecrPackages))
                    this.totalRows = this.ecrPackages.length
                    resolve()
                  },
                  error => {
                    reject(error)
                  }
                )
              },
              error => {
                reject(error)
              }
            )
          } else {
            this.getEcrPackages({ portalId: this.$refs.filterSearch.portalId, queryParams: params }).then(
              () => {
                this.ecrItems = JSON.parse(JSON.stringify(this.ecrPackages))
                this.totalRows = this.ecrPackages.length
                resolve()
              },
              error => {
                reject(error)
              }
            )
          }
        })
      },
      onFiltered (filteredItems) {
        this.totalRows = filteredItems.length
      },
      rowHowered (item) {
        item.actions.visible = true
      },
      rowUnhowered (item) {
        item.actions.visible = false
      },
      getDateTooltip (createDateTime, modifyDateTime) {
        const create = this.moment(createDateTime).format('DD.MM.YYYY')
        const modify = this.moment(modifyDateTime).format('DD.MM.YYYY')
        if (create === modify) {
          return 'Дата создания'
        } else {
          return 'Дата создания/Дата модификации'
        }
      },
      getDate (createDateTime, modifyDateTime) {
        const create = this.moment(createDateTime).format('DD.MM.YYYY')
        const modify = this.moment(modifyDateTime).format('DD.MM.YYYY')
        if (create === modify) {
          return create
        } else {
          return create + '/' + modify
        }
      },
      mySortCompare (aRow, bRow, key) {
        const a = aRow[key]
        const b = bRow[key]
        if (key === 'modify_datetime') {
          return new Date(b) - new Date(a)
        }
      },
      showDeclineReasons (declineReasons) {
        this.declineReasons = declineReasons.filter((item) => {
          return item.ResultCategory.toUpperCase() === 'ERROR'
        })
        this.$root.$emit('bv::show::modal', 'declineReasonsEcr', event)
      },
      editPackage (row) {
        this.getPackageDocumentsList(row.item.pid).then(
          () => {
            const hasEcr = this.packageDocumentsList.find((item) => {
              return item.type.toLowerCase() === 'expresscargodeclarationptd.cfg.xml'
            })
            if (!(hasEcr === undefined)) {
              this.$router.push(`/ecr/${row.item.pid}`)
            } else {
              this.$bvToast.toast('Обратитесь в техническую поддержку.', {
                title: 'Редактирование пакета невозможно!',
                toaster: 'b-toaster-bottom-right',
                solid: true,
                appendToast: true
              })
            }
          },
          (error) => {
            console.log(error)
          }
        )
      },
      editPackageAsNew (row) {
        this.getPackageDocumentsList(row.item.pid).then(
          () => {
            const hasEcr = this.packageDocumentsList.find((item) => {
              return item.type.toLowerCase() === 'expresscargodeclarationptd.cfg.xml'
            })
            if (!(hasEcr === undefined)) {
              this.$router.push({ path: `/ecr/${row.item.pid}`, query: { new: 'true' } })
            } else {
              this.$bvToast.toast('Обратитесь в техническую поддержку.', {
                title: 'Редактирование пакета невозможно!',
                toaster: 'b-toaster-bottom-right',
                solid: true,
                appendToast: true
              })
            }
          },
          (error) => {
            console.log(error)
          }
        )
      },
      openDocuments (row) {
        row.toggleDetails()
        if (row.item.documents === null) {
          this.loadPackageDocuments({ portalId: this.$refs.filterSearch.portalId, package: row.item.pid }).then(
            () => {
              row.item.documents = this.documents
              this.processDocuments(row)
            },
            error => {
              console.log(error)
            }
          )
        } else {
          row.item.documents = null
        }
      },
      openDocument (document) {
        this.documentFileName = document.type
        this.documentHtml = document.htmlAfterProcessing
        this.documentXml = document.xml
        this.documentId = document.pid
        this.documentName = document.documentName.replace('<br>', ' ')
        if (document.htmlForPdfCommissionShipment && document.htmlForPdfCommissionShipment.length > 0) {
          this.documentHtmlForPdf = document.htmlForPdfCommissionShipment
        } else {
          this.documentHtmlForPdf = document.htmlForPdf
        }
        this.documentAdditionalTable = document.additionalTable
        this.documentGeneralInformation = document.generalInformation
        this.mergedDocumentXml = document.mergedDocumentXml
        this.$root.$emit('bv::show::modal', 'openDocumentEcr', event)
      },
      resetProps () {
        this.documentHtml = null
        this.documentName = null
        this.documentXml = null
        this.documentId = null
        this.documentHtmlForPdf = null
      },
      ...mapMutations({
        setDeclineReasons: 'packages/setDeclineReasons',
        setVisible: 'ecr/setVisible'
      }),
      ...mapActions({
        getStatuses: 'packages/getStatuses',
        getEcrPackages: 'ecr/getEcrPackages',
        getPackageDocumentsList: 'ecr/getPackageDocumentsList',
        loadPackageDocuments: 'ecr/loadPackageDocuments',
      })
    }
  }
</script>

<style scoped>

</style>
